import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessengerWidget() {
  const { authHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendError, setSendError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/messages`, { headers: authHeaders });
        setMessages(r.data);
      } catch (e) { /* non-fatal — widget just shows empty state */ }
      setLoading(false);
    };
    load();
  }, [authHeaders]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setSendError(null);
    try {
      const r = await axios.post(`${API}/messages`, { body }, { headers: authHeaders });
      setMessages(prev => [...prev, { ...r.data, unread: false }]);
      setDraft('');
    } catch (e) {
      setSendError("Couldn't send — try again");
    }
    setSending(false);
  };

  const hasUnread = messages.some(m => m.unread);

  return (
    <Card data-testid="messenger-widget" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-fern" />
          <h3 className="font-heading text-lg font-semibold text-fern-dark">Message NestWise</h3>
          {hasUnread && <span data-testid="messenger-unread-dot" className="w-2 h-2 rounded-full bg-fern" />}
        </div>
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            <ScrollArea className="h-48 mb-3 pr-2">
              <div className="space-y-3">
                {messages.length === 0 && (
                  <p className="text-sm text-slate-400">No messages yet — ask us anything!</p>
                )}
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`rounded-2xl px-4 py-2 max-w-[80%] text-sm ${
                      m.sender === 'admin' ? 'bg-slate-100 text-slate-700' : 'bg-fern text-white'
                    }`}>
                      {m.body}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>
            <div className="flex gap-2">
              <Textarea
                data-testid="messenger-input"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="Type a message…"
                className="min-h-[44px] resize-none"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <Button
                data-testid="messenger-send"
                onClick={send}
                disabled={sending || !draft.trim()}
                className="bg-fern hover:bg-fern-light text-white rounded-full shrink-0 self-end"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {sendError && (
              <p data-testid="messenger-send-error" className="text-xs text-red-500 mt-2">
                {sendError}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
